import React, { useState, useEffect } from 'react';
import { Car, Home, Package, RefreshCw, Calendar, Clock, Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import FinSheet, { fmtINR } from './FinSheet';
import FinMktKit from './FinMktKit';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 17) return 'Good Afternoon';
  return 'Good Evening';
};

const LiveClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="flex items-center gap-4 text-slate-500 text-sm flex-wrap">
      <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" />{now.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
      <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" />{now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true })}</span>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, sub, color, trend }) => (
  <Card className="border-0 shadow-md hover:shadow-lg transition-shadow">
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wide truncate">{label}</p>
          <p className="text-2xl font-bold text-slate-900 mt-1 truncate">{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5 truncate">{sub}</p>}
        </div>
        <div className={`p-3 rounded-xl ml-3 shrink-0 ${color}`}><Icon className="w-5 h-5 text-white" /></div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
          {trend >= 0 ? <ArrowUpRight className="w-3.5 h-3.5" /> : <ArrowDownRight className="w-3.5 h-3.5" />}
          <span>{Math.abs(trend)}%</span><span className="text-slate-400 font-normal ml-1">vs last month</span>
        </div>
      )}
    </CardContent>
  </Card>
);

const useDataset = (key) => {
  const [rows, setRows] = useState([]);
  const [cols, setCols] = useState([]);
  useEffect(() => {
    (async () => {
      try {
        const [cRes, rRes] = await Promise.all([
          fetch(`${API}/finance/datasets/${key}/columns`),
          fetch(`${API}/finance/datasets/${key}/rows`),
        ]);
        setCols((await cRes.json()).columns || []);
        setRows((await rRes.json()).rows || []);
      } catch {}
    })();
  }, [key]);
  const colMap = Object.fromEntries(cols.map(c => [c.name.toUpperCase().trim(), c.id]));
  const getVal = (row, name) => row.data?.[colMap[name.toUpperCase().trim()]] ?? '';
  return { rows, getVal };
};

const SubTabs = ({ tabs, active, onSelect }) => (
  <div className="flex gap-2 mb-4 border-b border-slate-200 pb-3 flex-wrap">
    {tabs.map(s => (
      <button key={s.key} onClick={() => onSelect(s.key)}
        data-testid={`subtab-${s.key}`}
        className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition-colors ${active === s.key ? s.active : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
        {s.label}
      </button>
    ))}
  </div>
);

const FinanceDashboard = ({ user, embedded = false }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [kokanSub, setKokanSub] = useState('index');
  const [rhSub, setRhSub] = useState('index');
  const [mktSub, setMktSub] = useState('index');

  const kokanPayment = useDataset('fin_kokan_payment');
  const kokanIndex   = useDataset('fin_kokan_index');
  const rhBooking    = useDataset('fin_rh_booking');
  const rhIndex      = useDataset('fin_rh_index');
  const mktIndex     = useDataset('fin_mkt_index');
  const mktStmt      = useDataset('fin_mkt_statement');

  const kokanTotalPayable = kokanPayment.rows.reduce((s, r) => s + (parseFloat(kokanPayment.getVal(r,'PAYABLE')) || 0), 0);
  const kokanTotalPaid    = kokanPayment.rows.reduce((s, r) => s + (parseFloat(kokanPayment.getVal(r,'PAID')) || 0), 0);
  // Received & Balance Recovery come from the Payment Collection Index (DM/ADM wise)
  const kokanReceived     = kokanIndex.rows.reduce((s, r) => s + (parseFloat(kokanIndex.getVal(r,'RECEIVED')) || 0), 0);
  const kokanBalRecovery  = kokanIndex.rows.reduce((s, r) => s + (parseFloat(kokanIndex.getVal(r,'BALANCE RECOVERY')) || 0), 0);
  const rhTotalBookings   = rhBooking.rows.length;
  const rhTotalReceived   = rhBooking.rows.reduce((s, r) => s + (parseFloat(rhBooking.getVal(r,'RECEIVED')) || 0), 0);
  const rhTotalPaid       = rhBooking.rows.reduce((s, r) => s + (parseFloat(rhBooking.getVal(r,'PAID')) || 0), 0);
  const mktTotalKitPaid   = mktIndex.rows.reduce((s, r) => s + (parseFloat(mktIndex.getVal(r,'TOTAL KIT PAID')) || 0), 0);
  const mktTotalAmt       = mktIndex.rows.reduce((s, r) => s + (parseFloat(mktIndex.getVal(r,'TOTAL AMOUNT')) || 0), 0);
  const mktStmtDebit      = mktStmt.rows.reduce((s, r) => s + (parseFloat(mktStmt.getVal(r,'DEBIT')) || 0), 0);

  // Kokan DM/ADM-wise Received vs Balance Recovery
  const kokanDmData = kokanIndex.rows.map(r => ({
    name: (kokanIndex.getVal(r,'DM/ADM NAME') || '—').split(' ')[0],
    received: parseFloat(kokanIndex.getVal(r,'RECEIVED')) || 0,
    balance:  parseFloat(kokanIndex.getVal(r,'BALANCE RECOVERY')) || 0,
  })).filter(d => d.name !== '—' && (d.received > 0 || d.balance > 0)).slice(0, 10);

  // Marketing DM-wise Kit Paid
  const mktDmData = mktIndex.rows.map(r => ({
    name: (mktIndex.getVal(r,'DM NAME') || mktIndex.getVal(r,'MARKETING EXECUTIVE NAME') || '—').split(' ')[0],
    paid: parseFloat(mktIndex.getVal(r,'TOTAL KIT PAID')) || 0,
  })).filter(d => d.name !== '—' && d.paid > 0).slice(0, 12);

  const rhDmData = rhIndex.rows.slice(0, 8).map(r => ({
    name: (rhIndex.getVal(r,'DM/ADM NAME') || '—').split(' ')[0],
    received: parseFloat(rhIndex.getVal(r,'TOTAL RECEIVED')) || 0,
    balance: parseFloat(rhIndex.getVal(r,'BALANCE RECOVERY')) || 0,
  })).filter(d => d.received > 0 || d.balance > 0);

  const rhReceivable   = rhIndex.rows.reduce((s, r) => s + (parseFloat(rhIndex.getVal(r,'TOTAL RECEIVABLE')) || 0), 0);
  const rhIdxReceived  = rhIndex.rows.reduce((s, r) => s + (parseFloat(rhIndex.getVal(r,'TOTAL RECEIVED')) || 0), 0);
  const rhBalRecovery  = rhIndex.rows.reduce((s, r) => s + (parseFloat(rhIndex.getVal(r,'BALANCE RECOVERY')) || 0), 0);
  const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);
  const kokanPaidPct   = pct(kokanTotalPaid, kokanTotalPayable);
  const rhReceivedPct  = pct(rhIdxReceived, rhReceivable || (rhIdxReceived + rhBalRecovery));

  const EmptyChart = ({ msg }) => (
    <div className="flex items-center justify-center h-44 text-slate-400 text-sm italic">{msg}</div>
  );

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      {!embedded && (
        <div className="space-y-1">
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight leading-none">
            {getGreeting()}, {user?.firstName || 'Snehal'}!
          </h1>
          <p className="text-slate-400 text-base">Welcome to your dashboard. Here&apos;s what&apos;s happening today.</p>
        </div>
      )}

      <div className="flex flex-wrap items-end justify-between gap-4 border-b border-slate-100 pb-4">
        <div>
          <h2 className="text-3xl font-black text-slate-900">{embedded ? 'Finance — Executive Sheets' : 'Finance Dashboard'}</h2>
          <p className="text-slate-400 text-sm mt-0.5">{embedded ? 'Live view of Snehal\u2019s finance sheets · view, edit & import' : 'Kokan Travel · Rowhouse Bookings · Marketing Kit'}</p>
          {!embedded && <div className="mt-2"><LiveClock /></div>}
        </div>
        <Button variant="outline" size="sm" className="h-9 gap-2 border-slate-300" onClick={() => window.location.reload()}>
          <RefreshCw className="w-4 h-4" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100 p-1 rounded-xl h-auto flex-wrap gap-1">
          <TabsTrigger value="overview"  className="rounded-lg px-5 py-2 text-sm font-semibold data-[state=active]:bg-slate-900 data-[state=active]:text-white" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="kokan"     className="rounded-lg px-5 py-2 text-sm font-semibold data-[state=active]:bg-blue-700 data-[state=active]:text-white"   data-testid="tab-kokan">Kokan Travel Advance</TabsTrigger>
          <TabsTrigger value="rowhouse"  className="rounded-lg px-5 py-2 text-sm font-semibold data-[state=active]:bg-emerald-700 data-[state=active]:text-white" data-testid="tab-rowhouse">Rowhouse Booking Details</TabsTrigger>
          <TabsTrigger value="marketing" className="rounded-lg px-5 py-2 text-sm font-semibold data-[state=active]:bg-amber-600 data-[state=active]:text-white"   data-testid="tab-marketing">Marketing Kit Report</TabsTrigger>
        </TabsList>

        {/* ─── OVERVIEW ─────────────────────────────────────────────────────── */}
        <TabsContent value="overview" className="mt-6 space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard icon={Car}    label="Kokan Total Paid"    value={fmtINR(kokanTotalPaid)} sub={`Paid ${kokanPaidPct}% · Received ${fmtINR(kokanReceived)} · Bal. recovery ${fmtINR(kokanBalRecovery)}`} color="bg-blue-500" trend={2.1} />
            <StatCard icon={Home}   label="Rowhouse Received" value={fmtINR(rhIdxReceived)}   sub={`Receivable ${fmtINR(rhReceivable)} · Recovery ${fmtINR(rhBalRecovery)} · ${rhReceivedPct}% received`} color="bg-emerald-500" trend={5.4} />
            <StatCard icon={Package} label="Total Kit Paid (DM-wise)" value={fmtINR(mktTotalKitPaid)} sub={`${mktDmData.length} DMs · Kit amount ${fmtINR(mktTotalAmt)}`} color="bg-amber-500" trend={-1.2} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="border-0 shadow-md lg:col-span-2">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Car className="w-4 h-4 text-blue-600" />Kokan Travel — DM/ADM-wise Received vs Balance Recovery</CardTitle></CardHeader>
              <CardContent>
                {kokanDmData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={kokanDmData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} interval={0} angle={-20} textAnchor="end" height={50} />
                      <YAxis tickFormatter={v => fmtINR(v)} tick={{ fontSize: 10 }} width={60} />
                      <Tooltip formatter={(v, n) => [fmtINR(v), n === 'received' ? 'Received' : 'Balance Recovery']} />
                      <Bar isAnimationActive={false} dataKey="received" fill="#10b981" name="Received" radius={[3,3,0,0]} />
                      <Bar isAnimationActive={false} dataKey="balance"  fill="#f59e0b" name="Balance Recovery" radius={[3,3,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart msg="Enter data in Kokan Payment Collection Index to see chart" />}
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Package className="w-4 h-4 text-amber-600" />Marketing — DM-wise Kit Paid</CardTitle></CardHeader>
              <CardContent>
                {mktDmData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={mktDmData} layout="vertical" margin={{ top: 0, right: 10, left: 55, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tickFormatter={v => fmtINR(v)} tick={{ fontSize: 9 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={55} />
                      <Tooltip formatter={(v) => [fmtINR(v), 'Kit Paid']} />
                      <Bar isAnimationActive={false} dataKey="paid" fill="#f59e0b" radius={[0,3,3,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart msg="Enter data in Marketing Kit Index to see chart" />}
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Home className="w-4 h-4 text-emerald-600" />Rowhouse — DM-wise Received vs Balance</CardTitle></CardHeader>
              <CardContent>
                {rhDmData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={rhDmData} layout="vertical" margin={{ top: 0, right: 10, left: 60, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                      <XAxis type="number" tickFormatter={v => fmtINR(v)} tick={{ fontSize: 9 }} />
                      <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} />
                      <Tooltip formatter={(v, n) => [fmtINR(v), n === 'received' ? 'Received' : 'Balance']} />
                      <Bar isAnimationActive={false} dataKey="received" fill="#10b981" radius={[0,3,3,0]} />
                      <Bar isAnimationActive={false} dataKey="balance"  fill="#f59e0b" radius={[0,3,3,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart msg="Enter data in Rowhouse Index to see chart" />}
              </CardContent>
            </Card>
            <Card className="border-0 shadow-md">
              <CardHeader className="pb-2"><CardTitle className="text-base font-bold flex items-center gap-2"><Activity className="w-4 h-4 text-slate-600" />Financial Summary</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {[
                  { label: 'Kokan — Total Payable',   val: fmtINR(kokanTotalPayable), dot: 'bg-blue-500' },
                  { label: 'Kokan — Total Paid',      val: fmtINR(kokanTotalPaid),    dot: 'bg-blue-300' },
                  { label: 'RH — Total Received',     val: fmtINR(rhTotalReceived),   dot: 'bg-emerald-500' },
                  { label: 'Mkt Kit — Total Amt',     val: fmtINR(mktTotalAmt),       dot: 'bg-amber-500' },
                  { label: 'Mkt Statement Debit',     val: fmtINR(mktStmtDebit),      dot: 'bg-rose-500' },
                ].map(item => (
                  <div key={item.label} className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-2.5 h-2.5 rounded-full ${item.dot}`} />
                      <span className="text-sm text-slate-700">{item.label}</span>
                    </div>
                    <span className="font-bold text-slate-900 text-sm">{item.val}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── KOKAN TRAVEL ─────────────────────────────────────────────────── */}
        <TabsContent value="kokan" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-blue-900 text-white rounded-t-xl pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-700 rounded-lg"><Car className="w-5 h-5" /></div>
                <div><CardTitle className="text-lg font-bold">Kokan Travel Advance</CardTitle>
                  <p className="text-blue-200 text-xs mt-0.5">Payment Collection Index • Car Payment Excel</p></div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <SubTabs tabs={[
                { key: 'index',     label: 'Payment Collection Index',  active: 'bg-blue-700 text-white' },
                { key: 'payment',   label: 'Car Payment Excel',         active: 'bg-blue-700 text-white' },
              ]} active={kokanSub} onSelect={setKokanSub} />
              {kokanSub === 'index'     && <FinSheet datasetKey="fin_kokan_index"     accent="kokan" numericCols={['TOTAL PAYMENT PAYABLE','TOTAL PAYMENT PAID','COMPANY PART','DM PART','ADM PART','RECEIVED','BALANCE RECOVERY']} />}
              {kokanSub === 'payment'   && <FinSheet datasetKey="fin_kokan_payment"   accent="kokan" numericCols={['PAYABLE','PAID','KM','RATE','COMPANY','DM AMT','ADM AMT','BALANCE','CUMULATIVE TOTAL','CAR COUNT','TOTAL PERSONS','OFFICE CAR','SHREYASH TOURS & TRAVELS','SELF CAR','OTHER CAR']} />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── ROWHOUSE ─────────────────────────────────────────────────────── */}
        <TabsContent value="rowhouse" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-emerald-900 text-white rounded-t-xl pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-700 rounded-lg"><Home className="w-5 h-5" /></div>
                <div><CardTitle className="text-lg font-bold">Rowhouse Booking Details</CardTitle>
                  <p className="text-emerald-200 text-xs mt-0.5">Payment Index • Row House Booking</p></div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <SubTabs tabs={[
                { key: 'index',   label: 'Payment Index',     active: 'bg-emerald-700 text-white' },
                { key: 'booking', label: 'Row House Booking', active: 'bg-emerald-700 text-white' },
              ]} active={rhSub} onSelect={setRhSub} />
              {rhSub === 'index'   && <FinSheet datasetKey="fin_rh_index"   accent="rh" numericCols={['TOTAL CUSTOMER','TOTAL RECEIVABLE','TOTAL RECEIVED','BALANCE RECOVERY']} />}
              {rhSub === 'booking' && <FinSheet datasetKey="fin_rh_booking" accent="rh" numericCols={['ROW HOUSE','TENT','TOTAL DAYS','VEG','NON VEG','BREAKFAST','LUNCH','DINNER','OPENING','RECEIVED','PAID','CLOSING']} />}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── MARKETING KIT REPORT ─────────────────────────────────────────── */}
        <TabsContent value="marketing" className="mt-6">
          <Card className="border-0 shadow-md">
            <CardHeader className="bg-amber-900 text-white rounded-t-xl pb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-700 rounded-lg"><Package className="w-5 h-5" /></div>
                <div><CardTitle className="text-lg font-bold">Marketing Kit Report</CardTitle>
                  <p className="text-amber-200 text-xs mt-0.5">Marketing Kit Index • Marketing KIT</p></div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              <SubTabs tabs={[
                { key: 'index',    label: 'Marketing Kit Index',    active: 'bg-amber-700 text-white' },
                { key: 'mktkit',   label: 'Marketing KIT',          active: 'bg-yellow-500 text-black font-black' },
              ]} active={mktSub} onSelect={setMktSub} />
              {mktSub === 'index'    && <FinSheet datasetKey="fin_mkt_index"          accent="mkt" numericCols={['CASH','BANK','CREDIT RECOVERY','TOTAL AMOUNT','TOTAL KIT PAID','BOOKING KIT','PAID KIT','CREDIT KIT','PENDING KIT']} />}
              {mktSub === 'mktkit'   && <FinMktKit />}
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>
    </div>
  );
};

export default FinanceDashboard;
